import { Controller } from "@hotwired/stimulus";
import { Turbo } from "@hotwired/turbo-rails";
import csrfFetch from "../lib/csrf_fetch";

// Starts a quiz on a homework or challenge from whichever element was clicked,
// which stays busy until the quiz loads and says why if it can't
export default class extends Controller {
  static targets = ["error"];
  static values = { url: String, topic: String, lesson: String };

  // Turbo snapshots the page while the quiz loads, so Back restores the button busy
  connect() {
    this.element
      .querySelectorAll("[aria-busy='true']")
      .forEach((trigger) => this.setBusy(trigger, false));
  }

  async start(event) {
    const trigger = event.currentTarget;
    if (this.starting) return;
    this.starting = true;
    this.setBusy(trigger, true);
    if (this.hasErrorTarget) this.errorTarget.hidden = true;

    const body = {
      quiz: { topic_id: this.topicValue },
    };
    if (this.hasLessonValue && this.lessonValue)
      body.quiz.lesson_id = this.lessonValue;

    // csrfFetch asks for JSON, which QuizzesController#create answers with the new
    // quiz's Location or a refusal; Rails' own error pages send an error key instead
    let reason;
    try {
      const response = await csrfFetch(this.urlValue, {
        method: "POST",
        body: JSON.stringify(body),
      });
      // Devise refuses an ended session with 401; the page's own sign-in redirect takes it from here
      if (response.status === 401) {
        this.#free(trigger);
        Turbo.visit(window.location.href, { action: "replace" });
        return;
      }

      const location = response.ok && response.headers.get("Location");
      if (location) {
        Turbo.visit(location);
        return;
      }

      const { refusal } = await response.json().catch(() => ({}));
      if (typeof refusal === "string" && refusal) reason = refusal;
      else console.error("Quiz create failed", response.status);
    } catch (failure) {
      console.error(failure);
    }

    this.#free(trigger);
    // Disabling the button dropped its focus, so a keyboard user would start again from the top
    trigger.focus();
    this.showError(reason);
  }

  #free(trigger) {
    this.starting = false;
    this.setBusy(trigger, false);
  }

  setBusy(trigger, busy) {
    if ("disabled" in trigger) trigger.disabled = busy;
    if (busy) trigger.setAttribute("aria-busy", "true");
    else trigger.removeAttribute("aria-busy");
  }

  showError(reason) {
    if (!this.hasErrorTarget) return;

    this.errorTarget.textContent =
      reason ?? "Couldn't start the quiz. Please try again.";
    this.errorTarget.hidden = false;
  }
}
